import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader} from 'reactstrap';
import {MagnifyingGlass, X, CaretDown, Plus, Prohibit, Check, UploadSimple} from '../../icons';
import {AddClientForm} from '../AddClientModal';
import {anchorAttr} from '../../../guides/anchors';
import {VIEW_ECONOMICS, VIEW_SUMMARY} from './economics/ClientEconomicsRow';

// Alcances de la lista.
export const SCOPE_MINE = 'mine';
export const SCOPE_AGENCY = 'agency';

// Vistas de la lista: el Resumen de siempre y, con el módulo Economía del cliente, la de economía.
export {VIEW_ECONOMICS, VIEW_SUMMARY};

// Criterios de orden. "Atención primero" es el default y el único que agrupa por severidad.
export const ORDER_ATTENTION = 'attention';
export const ORDER_NAME = 'name';
export const ORDER_SPEND = 'spend';
export const ORDER_GOALS_AT_RISK = 'goals_at_risk';
export const ORDER_ACTIVITY = 'activity';

export const ORDER_OPTIONS = [
    {value: ORDER_ATTENTION, labelKey: 'clients_v2.order_attention'},
    {value: ORDER_NAME, labelKey: 'clients_v2.order_name'},
    {value: ORDER_SPEND, labelKey: 'clients_v2.order_spend'},
    {value: ORDER_GOALS_AT_RISK, labelKey: 'clients_v2.order_goals_at_risk'},
    {value: ORDER_ACTIVITY, labelKey: 'clients_v2.order_activity'},
];

/**
 * Barra de controles del Listado de Clientes v2: alcance (Míos / Agencia), búsqueda, orden, filtro
 * de inactivos y el botón primario "Nuevo cliente". La columna Actividad ya no tiene toggle: se
 * muestra siempre (card 109Ga0e0).
 *
 * El alta de cliente reusa el formulario de la pantalla clásica (`AddClientForm`, que ya hace el
 * POST a `client/add`); acá solo cambia el disparador, porque el botón de v2 tiene su propio look.
 */
export function ClientsV2Toolbar(props) {
    const {t} = useTranslation();
    const [isAddOpen, setIsAddOpen] = useState(false);

    const toggleAdd = () => setIsAddOpen(open => !open);

    const handleClientAdded = (newClientId, newClientName) => {
        setIsAddOpen(false);
        if (props.onClientAdded) props.onClientAdded(newClientId, newClientName);
    };

    const renderScopeButton = (value, labelKey, count) => {
        const isActive = props.scope === value;
        return (
            <button
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`cv2-segmented__btn ${isActive ? '-active' : ''}`}
                onClick={() => props.onScopeChange(value)}
            >
                {t(labelKey)}
                <span className="cv2-segmented__count">{count}</span>
            </button>
        );
    };

    const renderScope = () => {
        // Sin permiso para ver el alcance de la agencia no hay nada que elegir: el segmented sale.
        if (!props.canViewAgencyScope) return null;
        return (
            <div className="cv2-segmented" role="tablist" aria-label={t('clients_v2.scope_aria')}>
                {renderScopeButton(SCOPE_MINE, 'clients_v2.scope_mine', props.mineCount)}
                {renderScopeButton(SCOPE_AGENCY, 'clients_v2.scope_agency', props.agencyCount)}
            </div>
        );
    };

    /**
     * Qué se mira de cada cliente: el Resumen de la operación o su economía. Sólo existe con el
     * módulo (`economicsEnabled`): sin él no hay otra vista que elegir y el control no se dibuja.
     *
     * Es un par de botones de ESTADO (`aria-pressed`), no pestañas: cambia las columnas de la misma
     * lista, no abre un panel (un `tablist` sin `tabpanel` promete lo que no hay, R-ecofe-30).
     */
    const renderView = () => {
        if (!props.economicsEnabled) return null;
        const renderViewButton = (value, labelKey) => {
            const isActive = props.view === value;
            return (
                <button
                    type="button"
                    aria-pressed={isActive}
                    className={`cv2-segmented__btn ${isActive ? '-active' : ''}`}
                    onClick={() => props.onViewChange(value)}
                    data-testid={`cv2-view-${value}`}
                >
                    {t(labelKey)}
                </button>
            );
        };
        return (
            <div className="cv2-segmented cv2-view" role="group" aria-label={t('clients_v2.economics.view_label')}>
                {renderViewButton(VIEW_SUMMARY, 'clients_v2.economics.view_summary')}
                {renderViewButton(VIEW_ECONOMICS, 'clients_v2.economics.view')}
            </div>
        );
    };

    /**
     * Toggle de la toolbar (hoy, el filtro de Inactivos).
     *
     * El estado se comunica por TRES señales a la vez, porque con una sola no se leía: relleno
     * violeta pleno, tilde en lugar del ícono propio, y el verbo del label ("Ver X" apagado → "X"
     * prendido). Dos botones de contorno gris con el mismo texto son indistinguibles de un vistazo.
     */
    const renderToggle = ({isOn, onToggle, icon, labelKey, labelOnKey, values, disabled}) => (
        <button
            type="button"
            className={`cv2-btn cv2-btn--toggle ${isOn ? '-on' : ''}`}
            aria-pressed={isOn}
            disabled={disabled}
            onClick={onToggle}
        >
            {isOn ? <Check size={16} weight="bold"/> : icon}
            {t(isOn ? labelOnKey : labelKey, values)}
        </button>
    );

    return (
        <div className="cv2-toolbar">
            {renderScope()}
            {renderView()}

            <div className="cv2-search">
                <MagnifyingGlass size={16} className="cv2-search__icon"/>
                <input
                    type="text"
                    value={props.search}
                    onChange={(e) => props.onSearchChange(e.target.value)}
                    placeholder={t('clients_v2.search_placeholder')}
                    aria-label={t('clients_v2.search_placeholder')}
                    autoComplete="off"
                />
                {props.search && (
                    <button
                        type="button"
                        className="cv2-search__clear"
                        onClick={() => props.onSearchChange('')}
                        aria-label={t('clients_v2.search_clear')}
                    >
                        <X size={13}/>
                    </button>
                )}
            </div>

            <div className="cv2-select">
                <select
                    value={props.order}
                    onChange={(e) => props.onOrderChange(e.target.value)}
                    aria-label={t('clients_v2.order_aria')}
                >
                    {ORDER_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                </select>
                <CaretDown size={14} className="cv2-select__caret"/>
            </div>

            {/* Filtro de clientes desactivados. Se muestra siempre que la agencia tenga alguno —
                aunque en el alcance actual no haya— porque un control que aparece y desaparece al
                cambiar de Míos a Agencia se lee como un estado del botón, no como otra lista.
                Sin inactivos en el alcance queda deshabilitado con el contador en 0; si está
                prendido nunca se deshabilita, para poder apagarlo. */}
            {props.hasInactiveClients && renderToggle({
                isOn: props.showInactive,
                onToggle: props.onToggleInactive,
                icon: <Prohibit size={16}/>,
                labelKey: 'clients_v2.inactive_toggle',
                labelOnKey: 'clients_v2.inactive_toggle_on',
                values: {n: props.inactiveCount},
                disabled: props.inactiveCount === 0 && !props.showInactive,
            })}

            <div className="cv2-toolbar__spacer"/>

            {/* La planilla carga la economía de VARIOS clientes a la vez: vive en la vista que la
                muestra, no en el Resumen. */}
            {props.economicsEnabled && props.view === VIEW_ECONOMICS && (
                <button type="button" className="cv2-btn" onClick={props.onImport} data-testid="cv2-eco-import">
                    <UploadSimple size={16}/>
                    {t('clients_v2.economics.import_cta')}
                </button>
            )}

            <button type="button" className="cv2-btn cv2-btn--primary" onClick={toggleAdd}
                    {...anchorAttr('clients_list_new_client')}>
                <Plus size={16} weight="bold"/>
                {t('clients_v2.new_client')}
            </button>

            <Modal isOpen={isAddOpen} toggle={toggleAdd}>
                <ModalHeader toggle={toggleAdd}>{t('add_client.modal_title')}</ModalHeader>
                <AddClientForm closeModal={toggleAdd} onClientAdded={handleClientAdded}/>
            </Modal>
        </div>
    );
}

export default ClientsV2Toolbar;
